/**
 * Client-side mirror of the `InvitationName` value object in
 * `@buffer-mono/entities` (`entities/src/invitation/value-objects/`).
 *
 * Frontends can't import `entities`, so the rules and the copy are duplicated
 * here to give the admin immediate feedback instead of a round trip. The
 * server is the authority: it re-runs the same rules on every invite and
 * resend, so drift here weakens the message, never the control.
 *
 * Why the rules exist: the name is rendered inside the team-invite email, so
 * it is text an admin can put in front of any email address over Buffer's own
 * mail domain (INC-442).
 */
export const MAX_INVITATION_NAME_LENGTH = 100

/**
 * Control characters, zero-width spacing, and bidi marks that reorder or hide
 * text. U+200C (ZWNJ) and U+200D (ZWJ) are deliberately allowed — Persian and
 * several Indic scripts require them to spell names correctly.
 */
/**
 * The two joiners a name is allowed to carry, removed before every rule below
 * runs. Stripping is what makes allowing them safe: a joiner is invisible, so
 * `buffer-help\u200C.com` reads as a domain but hides it from `DOMAIN_LIKE`,
 * whose suffix has to follow the dot immediately.
 */
const ALLOWED_JOINERS = /\u200C|\u200D/gu

/** The joiner-stripped form every rule below is tested against. */
function forMatching(name: string): string {
  return name.replace(ALLOWED_JOINERS, '')
}

const CONTROL_OR_INVISIBLE_CHARS =
  /\p{Cc}|\u00AD|\u200B|\u200E|\u200F|\u061C|[\u202A-\u202E]|[\u2066-\u2069]|\uFEFF/u

/** Emoji and standalone symbols (⚠ ✅ → +). Accented letters are unaffected. */
const EMOJI_OR_SYMBOL_CHARS = /\p{Extended_Pictographic}|\p{S}/u

/** `:` `/` `\` `@` — never in a name, and required by a URL or an address. */
const ADDRESS_PUNCTUATION = /[:/\\@]/u

/**
 * A bare domain, matched against known clickable suffixes so real surnames
 * written without a space (`St.Clair`, `J.Smith`) are not flagged as
 * addresses. Keep in step with the entities copy.
 */
const CLICKABLE_SUFFIXES = [
  'com',
  'net',
  'org',
  'io',
  'co',
  'info',
  'biz',
  'app',
  'dev',
  'me',
  'ly',
  'link',
  'click',
  'live',
  'site',
  'online',
  'shop',
  'store',
  'xyz',
  'top',
  'icu',
  'vip',
  'cc',
  'tk',
  'ml',
  'ga',
  'cf',
  'gq',
  'buzz',
  'zip',
  'mov',
  'help',
  'support',
  'email',
  'gg',
  'sh',
  'ru',
  'cn',
  'uk',
  'de',
  'fr',
  'es',
  'it',
  'nl',
  'br',
  'au',
  'ca',
  'jp',
].join('|')

const DOMAIN_LIKE = new RegExp(
  `[\\p{L}\\p{N}][\\p{L}\\p{N}-]*\\.(?:${CLICKABLE_SUFFIXES})\\b`,
  'iu',
)

/** At least one letter or digit, so `...` and `---` are not names. */
const NAME_CONTENT = /[\p{L}\p{N}]/u

const ADDRESS_MESSAGE = 'Name cannot contain a web or email address.'

/**
 * Returns the user-facing reason a name is rejected, or `null` when it is
 * acceptable. An empty name returns `null` — "required" is handled separately
 * so an untouched field doesn't read as invalid.
 */
export function validateInvitationName(raw: string): string | null {
  const name = raw.trim()

  if (name === '') return null

  if (name.length > MAX_INVITATION_NAME_LENGTH) {
    return `Name cannot exceed ${MAX_INVITATION_NAME_LENGTH} characters.`
  }

  const probe = forMatching(name)
  if (CONTROL_OR_INVISIBLE_CHARS.test(probe)) {
    return 'Name cannot contain line breaks or invisible characters.'
  }
  if (EMOJI_OR_SYMBOL_CHARS.test(probe)) {
    return 'Name cannot contain emoji or symbols.'
  }
  if (ADDRESS_PUNCTUATION.test(probe) || DOMAIN_LIKE.test(probe)) {
    return ADDRESS_MESSAGE
  }
  if (!NAME_CONTENT.test(probe)) {
    return 'Name must contain at least one letter or digit.'
  }

  return null
}
